import type { WorkflowInvoiceSourceLineCandidate } from '../../types/workflowInvoice';

export interface RankedSourceLine {
  candidate: WorkflowInvoiceSourceLineCandidate;
  score: number;
}

export interface RankingOptions {
  /** Physical quantity already read/entered on the invoice line (szt./m²/m³). */
  quantity?: { pcs?: number | null; m2?: number | null; m3?: number | null } | null;
  /** Source lines already chosen by other invoice lines: ranked last, never recommended. */
  takenIds?: ReadonlySet<string>;
}

export interface SourceLineRanking {
  ranked: RankedSourceLine[];
  /** Set only when exactly one candidate agrees with every dimension in the invoice name. */
  recommendedId: string | null;
}

const NUMBER = '(\\d+(?:[.,]\\d+)?)';
const DIMENSION_RUN = new RegExp(`${NUMBER}\\s*[x×]\\s*${NUMBER}(?:\\s*[x×]\\s*${NUMBER})?`, 'i');

const toNumber = (raw: string) => Number(raw.replace(',', '.'));

/** "…EXTERIOR 12X1250X2500 MM" -> [12, 1250, 2500]; empty when the name carries no AxB(xC) run. */
export function parseNameDimensions(name: string | null | undefined): number[] {
  const match = DIMENSION_RUN.exec(name ?? '');
  if (!match) return [];
  return match.slice(1).filter(Boolean).map(toNumber).filter(Number.isFinite);
}

function candidateNumbers(candidate: WorkflowInvoiceSourceLineCandidate): number[] {
  const geometry = [candidate.width, candidate.height, candidate.length, candidate.thickness]
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const text = `${candidate.variantLabel ?? ''} ${candidate.productName ?? ''}`;
  const fromText = (text.match(/\d+(?:[.,]\d+)?/g) ?? []).map(toNumber);
  return [...geometry, ...fromText];
}

const words = (text: string | null | undefined) =>
  new Set((text ?? '').toLowerCase().split(/[^a-ząćęłńóśźż0-9]+/i).filter(word => word.length >= 3 && !/^\d+$/.test(word)));

function dimensionAgreement(wanted: number[], candidate: WorkflowInvoiceSourceLineCandidate): number {
  if (wanted.length === 0) return 0;
  const pool = candidateNumbers(candidate);
  let hits = 0;
  for (const value of wanted) {
    const at = pool.indexOf(value);
    if (at >= 0) { hits += 1; pool.splice(at, 1); }
  }
  return hits / wanted.length;
}

const sameQuantity = (a: number, b: number) => Math.abs(a - b) < 0.0005;

/** True when the invoice quantity equals the uninvoiced remainder of the source line in any shared unit. */
function matchesRemainder(
  quantity: RankingOptions['quantity'],
  candidate: WorkflowInvoiceSourceLineCandidate,
): boolean {
  if (!quantity) return false;
  const remaining = candidate.remainingQuantitySet
    ?? (candidate.remainingUnit && candidate.remainingQuantity != null ? { pcs: null, m2: null, m3: null, [candidate.remainingUnit]: candidate.remainingQuantity } : null);
  if (!remaining) return false;
  return (['pcs', 'm2', 'm3'] as const).some(unit => {
    const wanted = quantity[unit];
    const available = (remaining as Record<string, number | null | undefined>)[unit];
    return typeof wanted === 'number' && wanted > 0 && typeof available === 'number' && sameQuantity(wanted, available);
  });
}

/**
 * Orders candidates by how well they fit the name printed on the invoice
 * (dimensions first, shared words second). Pure presentation: it never selects.
 */
export function rankSourceLines(
  invoiceName: string | null | undefined,
  candidates: WorkflowInvoiceSourceLineCandidate[],
  options: RankingOptions = {},
): SourceLineRanking {
  const wanted = parseNameDimensions(invoiceName);
  const nameWords = words(invoiceName);
  const scored = candidates.map((candidate, index) => {
    const agreement = dimensionAgreement(wanted, candidate);
    const candidateWords = words(`${candidate.productName} ${candidate.variantLabel ?? ''}`);
    let shared = 0;
    nameWords.forEach(word => { if (candidateWords.has(word)) shared += 1; });
    const overlap = nameWords.size ? shared / nameWords.size : 0;
    const taken = options.takenIds?.has(candidate.sourceOrderLineId) ?? false;
    const quantityFit = matchesRemainder(options.quantity, candidate);
    return { candidate, index, agreement, overlap, taken, quantityFit, score: agreement * 10 + (quantityFit ? 5 : 0) + overlap - (taken ? 100 : 0) };
  });
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  const free = scored.filter(entry => !entry.taken);
  const byDimensions = free.filter(entry => wanted.length >= 2 && entry.agreement === 1);
  // Quantity alone is a weak signal: it only recommends a line that also shares a word with the invoice name.
  const byQuantity = free.filter(entry => entry.quantityFit && entry.overlap > 0);
  const pick = byDimensions.length === 1 ? byDimensions[0] : (wanted.length < 2 && byQuantity.length === 1 ? byQuantity[0] : null);
  return {
    ranked: scored.map(({ candidate, score }) => ({ candidate, score })),
    recommendedId: pick ? pick.candidate.sourceOrderLineId : null,
  };
}
